import store from '../../../store';
import { Shape } from './DrawingTools';
import { uuid, isToday } from "../../../helpers/common";
import { getFieldWorkWindow, clearShapeInfo, visibleWorkWindow } from '../../../actions/geo';

export default class GeoWorkWindow {
    constructor(map, google, drawingTool) {
        this.map = map;
        this.google = google;
        this.drawingTool = drawingTool;

        this.wwShapes = {};
        this.defaultShapeProperties = {
            fillColor: "#ffc107",
            fillOpacity: 1,
            strokeColor: "#ffc107",
            strokeWeight: 2,
            strokeOpacity: 1,
            zIndex: 99
        };

        const state = store.getState();
        this.reqParams = {
            farmId: state.geo.farmId,
            rangeId: state.analyze.searchParams.rangeId,
            selectedGeoShapeIds: []
        };

        this._geoShapeEventListener();
        this._storeSubscribe();
    }

    _storeSubscribe() {
        const me = this;
        this.storeUnsubscribe = store.subscribe(() => {
            const state = store.getState();
            const prevRangeId = me.reqParams.rangeId;
            const currentRangeId = state.analyze.searchParams.rangeId;

            if (prevRangeId !== currentRangeId) {
                me.reqParams.rangeId = currentRangeId;

                const farmId = state.geo.farmId;
                if (prevRangeId && me.reqParams.farmId && me.reqParams.farmId === farmId) {
                    for (const geoShapeRefId in state.geo.fieldWorkWindowById) {
                        const wwDetail = state.geo.fieldWorkWindowById[geoShapeRefId];

                        if (wwDetail.isWorkWindow || wwDetail.visible) {
                            const isExists = me.drawingTool.getShapeDetail(wwDetail.shapeId);
                            if (isExists) me.fetchWorkWindow(wwDetail);
                        }
                    }
                }
            }

            if (me.reqParams.farmId !== state.geo.farmId) {
                me.reqParams.farmId = state.geo.farmId;
                me.clearWorkWindows();
                store.dispatch(clearShapeInfo());
            }
        });
    }

    _geoShapeEventListener() {
        const me = this;
        this.drawingTool.addListener("shape_visible", "workwindow", ({ shapeId, visible }) => {
            if (!me.wwShapes[shapeId]) return;
            const shapeVisible = me.wwShapes[shapeId].visible;
            if (shapeVisible) me.visibleWorkWindow(shapeId, visible);
            me.wwShapes[shapeId].visible = shapeVisible;
        });
    }

    request(params) {
        if (params.geoShapeId && this.reqParams.selectedGeoShapeIds.indexOf(params.geoShapeId) < 0)
            this.reqParams.selectedGeoShapeIds.push(params.geoShapeId);
        return this;
    }

    getWorkWindow(params, isForceUpdate = false) {
        const { geoShapeRefId } = params;
        const state = store.getState();
        const wwDetail = state.geo.fieldWorkWindowById.hasOwnProperty(geoShapeRefId) ?
            state.geo.fieldWorkWindowById[geoShapeRefId] : null;

        if (!isForceUpdate) {
            const { rangeId, range } = state.analyze.searchParams;
            const isWorkWindow = (wwDetail && rangeId === wwDetail.rangeId) ?
                wwDetail.isWorkWindow : true;

            if (isToday(range) || wwDetail === null
                || (rangeId !== wwDetail.rangeId) || wwDetail.isRequesting) {
                const config = wwDetail && wwDetail.config ? wwDetail.config : null;
                this.fetchWorkWindow({ ...params, isWorkWindow, visible: true, config });
            }
            else if (wwDetail) {
                const { geoShapeId } = params;
                this.visibleWorkWindow(geoShapeId, wwDetail.isWorkWindow);
                store.dispatch(visibleWorkWindow(geoShapeRefId, true));
            }
        } else {
            const isWorkWindow = wwDetail ? wwDetail.isWorkWindow : true;
            this.fetchWorkWindow({ ...params, isWorkWindow, visible: true });
        }
    }

    fetchWorkWindow(params) {
        if (!params.geoShapeId) params.geoShapeId = params.shapeId;
        if (!params.geoShapeRefId) params.geoShapeRefId = params.shapeRefId;

        this.removeWorkWindow(params.geoShapeId).request(params);

        store.dispatch(getFieldWorkWindow({
            farmId: this.drawingTool.farmId,
            shapeGroupId: params.shapeGroupId,
            shapeId: params.geoShapeId,
            shapeRefId: params.geoShapeRefId,
            isWorkWindow: params.isWorkWindow,
            visible: params.visible,
            config: params.config,
            workTypeId: params.workTypeId
        }, response => {
            if (params.editable) return;
            if (!(response && response.grid)) return;

            const wwJSON = response.grid;
            if (!(wwJSON && wwJSON.hasOwnProperty("worked"))) return;

            const visible = params.isWorkWindow;
            this.bindWorkWindow(wwJSON, params.geoShapeId, visible);
        }));
    }

    bindWorkWindow(wwJSON, geoShapeId, visible) {
        if (this.reqParams.selectedGeoShapeIds.indexOf(geoShapeId) < 0) return this;

        const wwJSONParse = {};
        const pushItems = type => {
            if (wwJSON.hasOwnProperty(type) && Array.isArray(wwJSON[type])) {
                for (const window of wwJSON[type]) {
                    if (!wwJSONParse[geoShapeId])
                        wwJSONParse[geoShapeId] = { visible, workWindows: [] };

                    wwJSONParse[geoShapeId].workWindows.push({
                        windowId: uuid(),
                        shape: {
                            type: window.type.toLowerCase(),
                            location: window.coordinates[0].map(e => ({ lat: e[1], lng: e[0] })),
                            properties: {}
                        }
                    });
                }
            }
        }

        pushItems("worked");
        return this.drawWorkWindow(wwJSONParse);
    }

    drawWorkWindow(wwJSONParse) {
        const newWWShapes = {};
        const shapeObj = this.getShapeInstance();

        for (const geoShapeId in wwJSONParse) {
            const visible = wwJSONParse[geoShapeId].visible;

            for (const window of wwJSONParse[geoShapeId].workWindows) {
                const wwProps = {
                    ...window.shape,
                    id: window.windowId,
                    properties: {
                        ...this.defaultShapeProperties,
                        ...window.shape.properties
                    }
                };

                const shape = shapeObj.createShape(wwProps);
                if (!shape) continue;
                shape.type = wwProps.type;
                shape.addListener('click', e => {
                    if (!e) return;
                    const parentShape = this.drawingTool.getShape(geoShapeId);
                    if (parentShape) this.google.maps.event.trigger(parentShape, 'click', e);
                });
                if (visible) shape.setMap(this.map);

                const shapeId = shape.id;
                if (!newWWShapes[geoShapeId]) newWWShapes[geoShapeId] = { visible, shapes: {} };
                newWWShapes[geoShapeId].shapes[shapeId] = { shape };
            }
        }

        this.wwShapes = { ...this.wwShapes, ...newWWShapes };
        return this;
    }

    visibleWorkWindow(geoShapeId, visible) {
        if (!geoShapeId) return this;
        if (!this.wwShapes[geoShapeId]) return this;

        this.wwShapes[geoShapeId].visible = visible;
        for (const shapeId in this.wwShapes[geoShapeId].shapes) {
            const item = this.wwShapes[geoShapeId].shapes[shapeId];
            if (!item.shape) continue;

            if (visible) {
                if (!item.shape.getMap()) item.shape.setMap(this.map);
            }
            else {
                if (item.shape.getMap()) item.shape.setMap(null);
            }
        }
    }

    removeWorkWindow(geoShapeId) {
        if (!geoShapeId) return this;
        if (this.reqParams.selectedGeoShapeIds.indexOf(geoShapeId) >= 0) {
            const removeIndex = this.reqParams.selectedGeoShapeIds.indexOf(geoShapeId);
            this.reqParams.selectedGeoShapeIds =
                Array.from(this.reqParams.selectedGeoShapeIds).splice(removeIndex, 1);
        }

        if (!this.wwShapes[geoShapeId]) return this;
        for (const shapeId in this.wwShapes[geoShapeId].shapes) {
            const item = this.wwShapes[geoShapeId].shapes[shapeId];
            if (item.shape && item.shape.getMap()) item.shape.setMap(null);
        }
        delete this.wwShapes[geoShapeId];
        return this;
    }

    clearWorkWindows() {
        this.reqParams.selectedGeoShapeIds = [];
        for (const geoShapeId in this.wwShapes) {
            for (const shapeId in this.wwShapes[geoShapeId].shapes) {
                const item = this.wwShapes[geoShapeId].shapes[shapeId];
                if (item.shape && item.shape.getMap()) item.shape.setMap(null);
            }
            delete this.wwShapes[geoShapeId];
        }
        return this;
    }

    dispose() {
        this.clearWorkWindows();
        if (this.storeUnsubscribe) this.storeUnsubscribe();
        this.drawingTool.removeListenerByName("workwindow");
        return this;
    }

    getShapeInstance() {
        if (!this.shapeObj) this.shapeObj = new Shape(this.google);
        return this.shapeObj;
    }
}